/**
 * Geo split — current-week signups by country.
 *
 * Backend shape:
 *   { geo: Array<{ country: string | null, n: number }> }
 * sourced from metrics.dim_user JOIN core.profiles_public on country.
 *
 * US-primary launch reference: the spec calls out that we use a
 * US-weighted market mix (~70-80% US) for cost projections and
 * dashboard reads (project memory 2026-05-28).
 */
import { formatNumber } from '@/lib/utils';

interface GeoRow {
  country: string | null;
  n: number;
}

export function GeoSplitCard({ data }: { data: { geo: GeoRow[] } }) {
  const rows = data.geo ?? [];
  if (rows.length === 0) {
    return (
      <section className="bg-card border rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-1">Geo split (current week)</h3>
        <p className="text-sm text-muted-foreground">
          No country-tagged signups this week yet.
        </p>
      </section>
    );
  }
  const total = rows.reduce((s, r) => s + (r.n || 0), 0) || 1;

  return (
    <section className="bg-card border rounded-lg p-6">
      <h3 className="text-lg font-semibold mb-1">Geo split (current week)</h3>
      <p className="text-xs text-muted-foreground mb-4">
        Signups by country, current ISO week. US-primary launch reference.
      </p>
      <table className="w-full text-sm">
        <tbody>
          {rows.slice(0, 10).map((r) => {
            const pct = (r.n / total) * 100;
            return (
              <tr key={r.country ?? '__unknown'} className="border-b last:border-0">
                <td className="py-2 pr-3 w-[30%]">
                  {r.country ?? <span className="text-muted-foreground italic">Unknown</span>}
                </td>
                <td className="py-2 pr-3 font-mono text-right w-[15%]">{formatNumber(r.n)}</td>
                <td className="py-2 pr-3 font-mono text-right w-[15%]">{pct.toFixed(0)}%</td>
                <td className="py-2 pr-3">
                  <div className="bg-muted rounded h-2 relative overflow-hidden">
                    <div className="bg-primary h-full" style={{ width: `${pct}%` }} />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}
